<template>
  <div class="content-wrapper view-tier-manage p-0">
    <div class="content">
      <div class="card mb-3">
        <div class="card-body">
          <h5 class="mb-3"><i class="fas fa-sliders-h"></i> ตั้งค่าระบบระดับลูกค้า</h5>
          <TierConfigForm />
        </div>
      </div>

      <div class="card">
        <div class="card-body">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <h4 class="mb-0"><i class="fas fa-crown"></i> จัดการระดับลูกค้า</h4>
            <b-button v-if="canManageTiers" variant="primary" @click="openCreateForm">
              + เพิ่มระดับ
            </b-button>
          </div>

          <b-alert :show="!!errorMessage" variant="danger">{{ errorMessage }}</b-alert>

          <b-table
            :items="sortedTierList"
            :fields="fields"
            :busy="isLoading"
            responsive
            hover
            show-empty
            empty-text="ยังไม่มีระดับลูกค้า"
          >
            <template #cell(thresholdAmount)="{ value }">
              {{ formatBaht(value) }}
            </template>
            <template #cell(voucherBatchId)="{ value }">
              <span v-if="!value" class="text-muted">—</span>
              <span v-else>{{ batchLabel(value) }}</span>
            </template>
            <template #cell(isActive)="{ value }">
              <b-badge :variant="value ? 'success' : 'secondary'">
                {{ value ? "ใช้งาน" : "ปิดใช้งาน" }}
              </b-badge>
            </template>
            <template #cell(actions)="{ item }">
              <b-button
                v-if="canManageTiers"
                size="sm"
                variant="outline-primary"
                class="mr-1"
                @click="openEditForm(item)"
              >
                แก้ไข
              </b-button>
              <b-button
                v-if="canManageTiers"
                size="sm"
                variant="outline-danger"
                @click="confirmDelete(item)"
              >
                ลบ
              </b-button>
            </template>
          </b-table>
        </div>
      </div>

      <b-modal
        id="tierFormModal"
        ref="tierFormModal"
        :title="editingTier ? 'แก้ไขระดับลูกค้า' : 'เพิ่มระดับลูกค้า'"
        size="lg"
        hide-footer
      >
        <!--
          key forces a fresh TierForm instance per open: TierForm's data()
          seeds `form` from the `tier` prop only once at creation, so without
          a key the SAME instance would persist across create -> edit ->
          edit-a-different-tier and keep showing the first tier's values.
        -->
        <TierForm
          :key="editingTier ? `tier-edit-${editingTier.id}` : 'tier-create'"
          :tier="editingTier"
          @saved="onTierSaved"
          @cancel="$refs.tierFormModal.hide()"
        />
      </b-modal>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import TierConfigForm from "@/components/tier/TierConfigForm.vue";
import TierForm from "@/components/tier/TierForm.vue";
import { PERMISSIONS } from "@/constants/roles";
import { tierErrorMessage } from "@/utils/tierErrors";

export default {
  name: "TierManagement",
  components: { TierConfigForm, TierForm },
  data() {
    return {
      isLoading: false,
      errorMessage: "",
      editingTier: null,
      fields: [
        { key: "rank", label: "ลำดับ", sortable: false, class: "text-right" },
        { key: "name", label: "ชื่อระดับ" },
        { key: "thresholdAmount", label: "ยอดขั้นต่ำ", class: "text-right" },
        { key: "voucherBatchId", label: "ชุดบัตรเมื่อขึ้นระดับ" },
        { key: "isActive", label: "สถานะ" },
        { key: "actions", label: "" },
      ],
    };
  },
  computed: {
    ...mapGetters("moduleTier", ["getTierList"]),
    ...mapGetters("moduleVoucher", ["getBatchList"]),
    ...mapGetters("moduleUser", { can: "can", getBranchInfo: "getBranchInfo" }),
    clinicUrl() {
      return this.$route.params.clinicUrl;
    },
    // The route itself already gates on tier.manage (see router/index.js —
    // this screen bounces anyone without it before it ever mounts), but the
    // action controls still check can() explicitly, same as
    // VoucherManagement.vue does for voucher.manage: one consistent gating
    // mechanism everywhere rather than trusting the route alone.
    canManageTiers() {
      const clinicId = this.getBranchInfo?.clinic?.id;
      return this.can(PERMISSIONS.TIER_MANAGE, { clinicId });
    },
    // The backend already returns tiers ordered by rank ASC (tierRepo.listTiers),
    // but sorting again here is cheap and keeps the table correct even if a
    // future caller passes includeInactive through some other path that
    // doesn't preserve order.
    sortedTierList() {
      return [...(this.getTierList || [])].sort((a, b) => a.rank - b.rank);
    },
  },
  methods: {
    ...mapActions("moduleTier", ["listTiers", "deleteTier"]),
    ...mapActions("moduleVoucher", ["fetchBatches"]),

    formatBaht(value) {
      return `${Number(value).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท`;
    },
    batchLabel(batchId) {
      const batch = (this.getBatchList || []).find(b => b.id === batchId);
      return batch ? `${batch.name} (${batch.codePrefix})` : `#${batchId}`;
    },

    async load() {
      this.isLoading = true;
      this.errorMessage = "";
      try {
        await Promise.all([
          this.listTiers({ clinicUrl: this.clinicUrl, includeInactive: true }),
          this.fetchBatches({ clinicUrl: this.clinicUrl }),
        ]);
      } catch (error) {
        this.errorMessage = tierErrorMessage(error) || "โหลดรายการระดับลูกค้าไม่สำเร็จ";
      } finally {
        this.isLoading = false;
      }
    },

    openCreateForm() {
      this.editingTier = null;
      this.$refs.tierFormModal.show();
    },
    openEditForm(tier) {
      this.editingTier = tier;
      this.$refs.tierFormModal.show();
    },
    async onTierSaved() {
      this.$refs.tierFormModal.hide();
      // createTier/updateTier do not commit to state.tierList themselves
      // (see stores/tier.js's header comment) — the list must be re-fetched
      // explicitly after any mutation.
      await this.load();
    },

    async confirmDelete(tier) {
      // Deleting a tier is consequential, not merely a list-row removal: its
      // members lose the tier's standing discount immediately, so the dialog
      // says so explicitly rather than asking a generic "confirm?".
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `ลบระดับ "${tier.name}" ใช่หรือไม่? สมาชิกที่อยู่ในระดับนี้จะสูญเสียส่วนลดประจำระดับทันที และการลบนี้ไม่สามารถย้อนกลับได้`,
        { title: "ยืนยันการลบระดับลูกค้า", okTitle: "ลบ", okVariant: "danger", cancelTitle: "ยกเลิก", centered: true }
      );
      if (!confirmed) return;

      this.errorMessage = "";
      try {
        await this.deleteTier({ clinicUrl: this.clinicUrl, tierId: tier.id });
        await this.load();
      } catch (error) {
        this.errorMessage = tierErrorMessage(error) || "ลบระดับลูกค้าไม่สำเร็จ";
      }
    },
  },
  created() {
    this.load();
  },
};
</script>
